/* global Bliss */

import _get from "lodash/get";
import _isString from "lodash/isString";
import _pick from "lodash/pick";
import _uniqueId from "lodash/uniqueId";

import "./accordion_block.scss";

export default class AccordionBlock {
  // AccordionBlock data schema
  // {
  //   title: < string, required >,
  //   text: < string: inline HTML, required >
  // }

  static get icons() {
    return {
      caret: require("!svg-inline-loader?classPrefix!@fortawesome/fontawesome-free/svgs/solid/square-caret-down.svg"),
      arrow: require("!svg-inline-loader?classPrefix!../../../stylesheets/icons/arrow-bottom.svg")
    };
  }

  static get toolbox() {
    return {
      title: "accordion",
      icon: AccordionBlock.icons.caret
    };
  }

  static get conversionConfig() {
    return {
      export: data => {
        return [data.title, data.text].filter(Boolean).join("<br>");
      },
      import: "text"
    };
  }

  static get enableLineBreaks() {
    return true;
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get sanitize() {
    return {
      // N.B.: skip sanitizing as this should not be HTML. It also avoids parsing & as &amp;
      title: true,
      text: {
        br: true
      }
    };
  }

  constructor({ data, _config, api, readOnly }) {
    this.api = api;
    this.readOnly = readOnly;

    this.data = this._normalizeData(data);

    this._selectors = {
      block: "pe-accordion",
      title: "pe-accordion-title",
      toggler: "pe-accordion-toggler",
      panel: "pe-accordion-panel",
      text: "pe-accordion-text"
    };

    this._blockElem = null;
  }

  // data => view
  render() {
    this._blockElem = this._buildBlockElem(this.data);

    // add edit listener to links
    const anchorsArray = Bliss.$("a", this._blockElem);
    anchorsArray.forEach(anchorElem => {
      this.api.listeners.on(anchorElem, "click", this._editLink.bind(this), false);
    });

    return this._blockElem;
  }

  // view => data
  save(blockContent) {
    const titleElem = blockContent.querySelector("." + this._selectors.title);
    const textElem = blockContent.querySelector("." + this._selectors.text);
    const blockData = {
      title: _get(titleElem, "value", ""),
      text: _get(textElem, "innerHTML", "")
    };

    // empty title or text then block is in error
    if (_get(textElem, "textContent", "").trim() === "" || blockData.title.trim() === "") {
      blockData._errors = {
        title: "composant " + this.api.i18n.t("title"),
        messages: ["texte manquant"]
      };
    }

    this._blockElem.closest(".ce-block").classList.toggle("ce-block--invalid", Boolean(blockData._errors));

    return blockData;
  }

  // never remove block on validation
  validate(_) {
    return true;
  }

  destroy() {
    // remove edit listeners on links
    const anchorsArray = Bliss.$("a", this._blockElem);
    anchorsArray.forEach(anchorElem => {
      this.api.listeners.off(anchorElem, "click", this._editLink.bind(this), false);
    });
  }

  // PRIVATE

  _normalizeData(data) {
    data = _pick(data, ["title", "text"]);

    data.title = _isString(data.title) ? data.title : "";
    data.text = this._forceInlineHTML(data.text);

    return data;
  }

  _forceInlineHTML(string) {
    if (!string) {
      return "";
    }

    // replace div and p by br
    return String(string)
      .replace(/<(div|p)[^>]*><br><\/?(div|p)[^>]?>/g, "<br>")
      .replace(/(<\/?(div|p)[^>]*>)+/g, "<br>");
  }

  _buildBlockElem(blockData) {
    // peat: paris editor accordion toggler
    const togglerId = _uniqueId("peat-");

    // N.B.: as enableLineBreaks is enabled for this plugin, we have to make sure that contenteditable tags are p
    // Then specific style is applied for line breaks to insert br to keep only inline tags in contenteditable
    // see discussion in issue #1212 (https://github.com/parisnumerique/paris-fr-v3/issues/1212)
    const blockElemOptions = {
      tag: "div",
      class: this._selectors.block,
      contents: [
        {
          tag: "input",
          type: "text",
          class: this._selectors.title,
          placeholder: "titre...",
          value: blockData.title,
          events: { keydown: this._disableEnter.bind(this) }
        },
        {
          tag: "input",
          type: "checkbox",
          id: togglerId,
          class: this._selectors.toggler
        },
        {
          tag: "label",
          for: togglerId,
          innerHTML: AccordionBlock.icons.arrow
        },
        {
          tag: "div",
          class: this._selectors.panel,
          contents: [
            {
              tag: "p",
              class: this._selectors.text,
              "data-placeholder": "texte...",
              contenteditable: true,
              innerHTML: blockData.text
            }
          ]
        }
      ]
    };

    if (this.readOnly) {
      blockElemOptions.contents[0].readonly = true;
      blockElemOptions.contents[1].readonly = true;
    }

    return Bliss.create(blockElemOptions);
  }

  // LISTENERS

  _disableEnter(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
    }
  }

  _editLink(_event) {
    const anchorElem = this.api.selection.findParentTag("A");

    if (anchorElem) {
      this.api.selection.expandToTag(anchorElem);
      this.api.inlineToolbar.open();
    }
  }
}
